import { type FC, type JSX, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import type { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import { getHasLongPrice } from '@/app/components/search/components/Card/Details/Price/PriceDetails/utils.ts';
import { isOpxDeal } from '@/app/components/search/components/Card/utils/getPromotionsUtils.ts';
import { getIsLoyaltyEnabled, hasLoyaltyPromotion, isLoyaltyUser } from '@/app/common/Loyalty/utils.ts';
import LoyaltyBadge from '@/app/common/components/LoyaltyBadge';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import TaxesIncludedTooltip from '../TaxesIncludedTooltip';
import {
  PriceAmount,
  PriceDetailsContainer,
  PriceDetailsWrapper,
  PriceDiscount,
  PriceDiscountPercentage,
  PriceTotalAmount,
  PriceUnit,
  ProductInfo,
} from './styled';
import type { DealCard } from '../../../../../utils/buildDealCards/dealCardTypes';
import { CardsContext } from '../../../../SearchPageContainer/ListContainer/CardsGrid/CardsContext';
import { getPriceDetails } from '../../../utils/getPriceDetails';

type PriceDetailsProps = {
  isOverlay: boolean;
  dealCard: DealCard;
  promotionTypes: PROMOTION_TYPES[];
  promotionType: PROMOTION_TYPES | null;
  showTotalPrice: boolean;
};

const PriceDetails: FC<PriceDetailsProps> = ({
  isOverlay,
  dealCard,
  promotionTypes,
  promotionType,
  showTotalPrice,
}): JSX.Element => {
  const {
    searchInfoData,
    localization,
    commonData,
    localization: {
      configValuesApp: { lastMinuteDeals },
    },
  } = usePageWrapperData();
  const { t } = useTranslation();
  const { isMobile } = useCommonClient();
  const { isWhiteLabel } = useContext(CardsContext);
  const { showTaxesIncludedTooltip, dismissTaxesIncludedTooltip, taxesIncludedTooltipClaimedRef } =
    useContext(SearchPageContext);
  const taxesIncludedPopup = localization?.configValuesApp?.taxesIncludedPopup;
  const isTooltipOwnerRef = useRef(false);
  const [isTooltipOwner, setIsTooltipOwner] = useState(false);
  const priceWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      showTaxesIncludedTooltip &&
      taxesIncludedPopup &&
      !isOverlay &&
      !isOpxDeal(dealCard) &&
      !taxesIncludedTooltipClaimedRef.current
    ) {
      taxesIncludedTooltipClaimedRef.current = true;
      isTooltipOwnerRef.current = true;
      setIsTooltipOwner(true);
    } else if (!showTaxesIncludedTooltip && isTooltipOwnerRef.current) {
      isTooltipOwnerRef.current = false;
      setIsTooltipOwner(false);
    }
  }, [showTaxesIncludedTooltip]);

  // The tooltip uses position:absolute and overflows below the card boundary.
  // Each CardContainer creates a stacking context with z-index:1, so the next card
  // (same z-index, later in DOM order) would paint on top of the overflow.
  // Elevating the owning card to z-index:2 makes its stacking context win over siblings.
  useEffect(() => {
    const card = priceWrapperRef.current?.closest<HTMLElement>('[data-testid="card-container"]');
    if (isTooltipOwner && card) {
      card.style.zIndex = '2';
    }
    return () => {
      if (card) card.style.zIndex = '';
    };
  }, [isTooltipOwner]);

  const isHO = searchInfoData?.searchMode === SearchModes.HO;
  const packageSavingsUspEnabled = localization?.configValuesApp?.packageSavingsUspEnabled;
  const hasPackageSavings = packageSavingsUspEnabled && dealCard?.product?.rate?.packageDiscount?.enabled;
  const isOverlayAndMobile = isOverlay && isMobile;
  const {
    productLabel,
    price,
    priceLabel,
    priceBeforeDiscount,
    totalPriceLabel,
    totalPrice,
    isHoOneNightStay,
    showDiscount,
    discountPercentage,
    totalPerNightsLabel,
  } = getPriceDetails({
    dealCard,
    searchInfoData,
    localization,
    commonData,
    isMobile,
    isOverlay,
    t,
  });

  const hasLongPrice = getHasLongPrice({ price, isMobile });
  const showPriceDiscount = !isOverlayAndMobile && showDiscount && !hasPackageSavings;
  const showPriceTotalAmount = showTotalPrice && !isHO;
  const priceAmount = isHoOneNightStay ? totalPrice : price;
  const showProductInfo = false;
  const samePpAndTotalPrice = showPriceTotalAmount && price === totalPrice;
  const isMyPrice = dealCard?.product?.rate?.flags?.myPrice;
  const showMyPricePercentage = isMyPrice && showDiscount && discountPercentage && !isMobile;
  const loyaltyEnabled = getIsLoyaltyEnabled(localization.configValuesApp);
  const loyaltyEnrolled = isLoyaltyUser(commonData);
  const loyaltyDeal = hasLoyaltyPromotion(promotionTypes);
  const showLoyaltyDiscount = loyaltyEnabled && loyaltyEnrolled && loyaltyDeal && showDiscount && discountPercentage;

  const basePriceProps = {
    promotionType,
    isWhiteLabel,
    lastMinuteDeals,
    isOverlay,
    hasLongPrice,
  };

  const product = showProductInfo ? <ProductInfo {...basePriceProps}>{productLabel}</ProductInfo> : null;
  const discount = showPriceDiscount ? <PriceDiscount {...basePriceProps}>{priceBeforeDiscount}</PriceDiscount> : null;
  const amount = priceAmount ? <PriceAmount {...basePriceProps}>{priceAmount}</PriceAmount> : null;
  const totalAmount = showPriceTotalAmount ? <PriceTotalAmount>{totalPriceLabel}</PriceTotalAmount> : null;
  const label = priceLabel ? <PriceUnit {...basePriceProps}>&nbsp;{priceLabel}</PriceUnit> : null;
  const myPriceDiscount = showMyPricePercentage ? (
    <PriceDiscountPercentage isOverlay={isOverlay} isMobile={isMobile}>
      {discountPercentage}
    </PriceDiscountPercentage>
  ) : null;
  const loyaltyDiscount = showLoyaltyDiscount ? <LoyaltyBadge label={`${discountPercentage}`} /> : null;
  const totalPerNights = totalPerNightsLabel ? (
    <PriceTotalAmount style={{ marginTop: 4 }}>{totalPerNightsLabel}</PriceTotalAmount>
  ) : null;

  const priceLayout = () => {
    if (samePpAndTotalPrice) {
      return (
        <>
          {discount}
          <PriceAmount {...basePriceProps}>{totalPrice}</PriceAmount>
        </>
      );
    }

    return (
      <>
        {product}
        {discount}
        {amount}
        {label}
        {totalAmount}
        {totalPerNights}
      </>
    );
  };

  return (
    <>
      {loyaltyDiscount}
      {myPriceDiscount}
      <PriceDetailsWrapper ref={priceWrapperRef}>
        <PriceDetailsContainer>{priceLayout()}</PriceDetailsContainer>
        {isTooltipOwner && <TaxesIncludedTooltip onDismiss={dismissTaxesIncludedTooltip} />}
      </PriceDetailsWrapper>
    </>
  );
};

export default PriceDetails;
